import { Link } from "./LocaleLink";

const P_CLS = "mt-4 font-body font-light text-lg text-foreground/80 leading-relaxed";
const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/g;
const STRIP_LANG = /^\/(en|fr|tr|es)(\/|$)/;

const inline = (text) => {
  const out = [];
  let last = 0, m, k = 0;
  while ((m = LINK_RE.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    const href = m[2];
    const cls = "text-primary underline decoration-primary/40 underline-offset-4 hover:decoration-primary transition-colors";
    if (href.startsWith("/")) {
      out.push(<Link key={k++} to={href.replace(STRIP_LANG, "/")} className={cls}>{m[1]}</Link>);
    } else {
      out.push(<a key={k++} href={href} target="_blank" rel="noopener noreferrer" className={cls}>{m[1]}</a>);
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
};

export const renderInline = inline;
export const stripMarkdown = (text) => String(text ?? "").replace(LINK_RE, "$1");

const isRow = (l) => l.trim().startsWith("|") && l.trim().endsWith("|");
const isSep = (l) => /^\|?\s*:?-{2,}:?\s*(\|\s*:?-{2,}:?\s*)*\|?$/.test(l.trim());
const cells = (l) => l.trim().replace(/^\||\|$/g, "").split("|").map((c) => c.trim());

const Table = ({ rows }) => {
  const [head, ...body] = rows.filter((r) => !isSep(r)).map(cells);
  return (
    <div className="mt-6 -mx-4 sm:mx-0 overflow-x-auto" data-testid="blog-table">
      <table className="min-w-full text-left border-collapse font-body text-sm sm:text-base">
        <thead>
          <tr className="border-b border-primary/60">
            {head.map((h, i) => <th key={i} className="py-3 px-3 font-accent text-[11px] uppercase tracking-[0.15em] text-primary whitespace-nowrap align-bottom">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {body.map((r, i) => (
            <tr key={i} className="border-b border-border/50 align-top">
              {r.map((c, j) => <td key={j} className={`py-3 px-3 text-foreground/80 ${j === 0 ? "font-medium text-foreground" : "font-light"}`}>{inline(c)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// Renders one stored paragraph string: plain text, "### " sub-heading, "- " bullets, markdown pipe tables, [links](url).
export const RichParagraph = ({ text }) => {
  const lines = String(text ?? "").split("\n");
  const blocks = [];
  for (let i = 0; i < lines.length; i++) {
    const l = lines[i];
    if (!l.trim()) continue;
    if (isRow(l)) {
      const rows = [];
      while (i < lines.length && isRow(lines[i])) rows.push(lines[i++]);
      i--;
      blocks.push(<Table key={blocks.length} rows={rows} />);
    } else if (/^\s*[-•]\s+/.test(l)) {
      const items = [];
      while (i < lines.length && /^\s*[-•]\s+/.test(lines[i])) items.push(lines[i++].replace(/^\s*[-•]\s+/, ""));
      i--;
      blocks.push(<ul key={blocks.length} className={`${P_CLS} list-disc pl-6 space-y-2`}>{items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>);
    } else if (/^###\s+/.test(l)) {
      blocks.push(<h3 key={blocks.length} className="mt-8 font-headings text-xl sm:text-2xl text-foreground">{l.replace(/^###\s+/, "")}</h3>);
    } else {
      blocks.push(<p key={blocks.length} className={P_CLS}>{inline(l.replace(/^\*\*(.+)\*\*:?$/, "$1"))}</p>);
    }
  }
  return <>{blocks}</>;
};
